import { AxiosInstance } from 'axios';
import Vue from 'vue';
import { NavigationGuard, RawLocation } from 'vue-router';
import { AuthStrategyType, RouteAccess } from '../../tenants/types';
import { installAuthInterceptors } from './auth-interceptors';
import { authStrategyFactory } from './authStrategyFactory';
import { AccessControl, AuthStrategy, routeAccessOf } from './types';

export interface AccessCoordinatorOptions {
    /** Accesos que usan las rutas habilitadas del tenant: define qué estrategias se crean. */
    accesses: RouteAccess[];
    /** Path del layout raíz (`/`). */
    rootPath: string;
    /** Nombre del parámetro de ruta que trae el token (rutas `RouteAccess.UrlToken`). */
    tokenParam: string;
    /** Paths de la sesión del tenant (`TenantConfig.session`). Obligatorio si hay rutas `RouteAccess.Session`. */
    session?: { loginPath: string; landingPath: string };
}

/**
 * Acceso por ruta. Cada ruta declara su `RouteAccess` (`meta.access`) y el coordinador usa la estrategia que
 * corresponde a la ruta activa, que fija el guard en cada navegación:
 * - `UrlToken` → `UrlTokenStrategy` (token del `:token`, 401 = rechazo, sin "Cerrar sesión");
 * - `Session` → `LoginStrategy` (OAuth2, 401 = refresh con cola / login);
 * - `Public` → `LoginStrategy` si el tenant tiene sesión (login con sesión → landing), si no `UrlTokenStrategy`.
 *
 * Solo crea las estrategias que piden las rutas del tenant. Instala los interceptores de `Http` una sola vez
 * (`install`): el `Authorization` se pone por request según la estrategia activa, nunca en `defaults`.
 */
export class AccessCoordinator implements AccessControl {
    private readonly sessionStrategy: AuthStrategy | null;
    private readonly urlTokenStrategy: AuthStrategy | null;
    /** Reactivo: los componentes que leen `supportsLogout` se actualizan al cambiar de ruta. */
    private readonly state = Vue.observable({ access: RouteAccess.Public as RouteAccess });
    private installed = false;

    constructor(options: AccessCoordinatorOptions) {
        const { accesses, rootPath, tokenParam, session } = options;
        if (accesses.includes(RouteAccess.Session) && !session) {
            throw new Error(
                '[auth] Hay rutas de sesión (`RouteAccess.Session`) y el tenant no define `session` en su config.'
            );
        }
        this.sessionStrategy = session
            ? authStrategyFactory(AuthStrategyType.Login, { rootPath, tokenParam, ...session })
            : null;
        this.urlTokenStrategy = accesses.includes(RouteAccess.UrlToken)
            ? authStrategyFactory(AuthStrategyType.UrlToken, { rootPath, tokenParam })
            : null;
    }

    /** Acceso de la ruta activa (el de la última navegación). */
    get activeAccess(): RouteAccess {
        return this.state.access;
    }

    /** Estrategia de la ruta activa (o `null` si el tenant no tiene ninguna para ese acceso). */
    get activeStrategy(): AuthStrategy | null {
        return this.strategyFor(this.state.access);
    }

    strategyFor(access: RouteAccess): AuthStrategy | null {
        switch (access) {
            case RouteAccess.Session:
                return this.sessionStrategy;
            case RouteAccess.UrlToken:
                return this.urlTokenStrategy;
            default:
                return this.sessionStrategy || this.urlTokenStrategy;
        }
    }

    /** Conecta el coordinador al cliente HTTP compartido. Se llama una sola vez al arrancar (idempotente). */
    install(http: AxiosInstance): void {
        if (this.installed) {
            return;
        }
        this.installed = true;
        installAuthInterceptors(http, () => this.activeStrategy);
    }

    get supportsLogout(): boolean {
        const strategy = this.activeStrategy;
        return !!strategy && strategy.supportsLogout;
    }

    getToken(): string | null {
        const strategy = this.activeStrategy;
        return strategy ? strategy.getToken() : null;
    }

    isAuthenticated(): boolean {
        const strategy = this.activeStrategy;
        return !!strategy && strategy.isAuthenticated();
    }

    logout(): RawLocation | null {
        const strategy = this.activeStrategy;
        return strategy ? strategy.logout() : null;
    }

    guard: NavigationGuard = (to, from, next) => {
        const access = routeAccessOf(to);
        const strategy = this.strategyFor(access);
        if (!strategy && access !== RouteAccess.Public) {
            // No debería pasar: `accessConfigErrors` / el constructor lo impiden al arrancar.
            throw new Error(`[auth] La ruta "${to.path}" es "${access}" y el tenant no tiene estrategia para ese acceso.`);
        }
        this.state.access = access;
        if (!strategy) {
            next();
            return;
        }
        strategy.guard(to, from, next);
    };
}
